// Same recurrence math as myfit-app's src/utils/dates.ts — kept in sync by
// hand since these are two separate codebases talking to the same
// myfit-api /myevents backend.

function pad(n) {
  return String(n).padStart(2, "0");
}

// "YYYY-MM-DDTHH:mm", the exact value <input type="datetime-local"> both
// reads and writes.
export function toDatetimeLocalValue(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
}

// myfit-api's date_time accepts datetime.fromisoformat() — the
// datetime-local value is already that shape, just missing seconds.
export function toApiDateTime(datetimeLocalValue) {
  if (!datetimeLocalValue) return undefined;
  return datetimeLocalValue.length === 16 ? `${datetimeLocalValue}:00` : datetimeLocalValue;
}

export function sameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** Whether `event` has an occurrence that lands on calendar day `date`. */
export function eventOccursOnDate(event, date) {
  const start = new Date(event.date_time);
  if (Number.isNaN(start.getTime())) return false;

  const startDay = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const day = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  if (day < startDay) return false;

  switch (event.repeat) {
    case "one_time":
      return sameDay(start, date);
    case "daily":
      return true;
    case "weekly":
      return start.getDay() === date.getDay();
    case "monthly":
      return start.getDate() === date.getDate();
    default:
      return sameDay(start, date);
  }
}

function timeOfDayMinutes(event) {
  const d = new Date(event.date_time);
  return d.getHours() * 60 + d.getMinutes();
}

export function eventsOnDate(events, date) {
  return events
    .filter((e) => eventOccursOnDate(e, date))
    .sort((a, b) => timeOfDayMinutes(a) - timeOfDayMinutes(b));
}

export function formatTime(dateTime) {
  const d = new Date(dateTime);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

export function formatDuration(minutes) {
  if (minutes < 60) return `${minutes} min`;
  const hrs = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hrs}h ${rest}m` : `${hrs}h`;
}

/** Next time `event` actually happens, from `from` onward (defaults to
 * now). `one_time` events return their own date_time, or null once it's
 * passed. Recurring events (daily/weekly/monthly) never expire — this
 * computes their next real-world occurrence, never earlier than the
 * series' own start date_time. */
export function nextOccurrence(event, from = new Date()) {
  const start = new Date(event.date_time);
  if (Number.isNaN(start.getTime())) return null;

  if (event.repeat === "one_time") {
    return start >= from ? start : null;
  }

  const hours = start.getHours();
  const minutes = start.getMinutes();
  let candidate;

  if (event.repeat === "daily") {
    candidate = new Date(from.getFullYear(), from.getMonth(), from.getDate(), hours, minutes);
    if (candidate < from) candidate.setDate(candidate.getDate() + 1);
  } else if (event.repeat === "weekly") {
    const targetDay = start.getDay();
    candidate = new Date(from.getFullYear(), from.getMonth(), from.getDate(), hours, minutes);
    candidate.setDate(candidate.getDate() + ((targetDay - candidate.getDay() + 7) % 7));
    if (candidate < from) candidate.setDate(candidate.getDate() + 7);
  } else if (event.repeat === "monthly") {
    const targetDate = start.getDate();
    candidate = new Date(from.getFullYear(), from.getMonth(), targetDate, hours, minutes);
    if (candidate < from) {
      candidate = new Date(
        candidate.getFullYear(),
        candidate.getMonth() + 1,
        targetDate,
        hours,
        minutes
      );
    }
  } else {
    return start >= from ? start : null;
  }

  return candidate < start ? start : candidate;
}
